import {
  FetchMFAPreferenceOutput,
  updateMFAPreference,
} from "aws-amplify/auth";
import { useState } from "react";

import { Card } from "@/components/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { User } from "@/types/user";
import { useQueryClient } from "@tanstack/react-query";

import { AddSmsModal } from "./add-sms-modal";
import { AddTotpModal } from "./add-totp-modal";
import { MfaSettingsRow } from "./mfa-settings-row";

type MfaSettingsFormProps = {
  initialData: FetchMFAPreferenceOutput & Pick<User, "phone_number" | "email">;
};

export function MfaSettingsForm({ initialData }: MfaSettingsFormProps) {
  const { enabled, preferred } = initialData;
  const queryClient = useQueryClient();
  const [totpModalOpen, setTotpModalOpen] = useState(false);
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const isSMS = enabled?.includes("SMS") || false;
  const isTOTP = enabled?.includes("TOTP") || false;
  const isEmail = enabled?.includes("EMAIL") || false;

  const isTOTPPreferred = preferred === "TOTP";
  const isSMSPreferred = preferred === "SMS";
  const isEmailPreferred = preferred === "EMAIL";

  const handleAddEmail = async () => {
    await updateMFAPreference({
      email: "ENABLED",
      sms: isSMS ? "ENABLED" : "DISABLED",
      totp: isTOTP ? "ENABLED" : "DISABLED",
    });

    await queryClient.refetchQueries({ queryKey: ["mfa"] });
  };

  return (
    <Card>
      <Card.Header
        title="Authentication"
        description="Manage your authentication methods"
      />
      <Card.Content className="divide-y p-0">
        {isTOTP && (
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <p className="text-xs text-foreground">Authenticator App</p>
              {isTOTPPreferred && <Badge variant="outline">Default</Badge>}
            </div>
            <MfaSettingsRow
              type="totp"
              isDefault={isTOTPPreferred}
              openModal={() => setTotpModalOpen(true)}
            />
          </div>
        )}
        {isSMS && (
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <p className="text-xs text-foreground">SMS</p>
              <Badge variant="outline">{initialData.phone_number}</Badge>
              {isSMSPreferred && <Badge variant="outline">Default</Badge>}
            </div>
            <MfaSettingsRow
              type="sms"
              isDefault={isSMSPreferred}
              openModal={() => setSmsModalOpen(true)}
            />
          </div>
        )}
        {isEmail && (
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center gap-2">
              <p className="text-xs text-foreground">Email</p>
              <Badge variant="outline">{initialData.email}</Badge>
              {isEmailPreferred && <Badge variant="outline">Default</Badge>}
            </div>
            <MfaSettingsRow
              type="email"
              isDefault={isEmailPreferred}
              openModal={() => setSmsModalOpen(true)}
            />
          </div>
        )}

        <div className={cn((!isSMS || !isTOTP) && "px-4 py-4")}>
          <AddTotpModal
            open={totpModalOpen}
            onOpenChange={setTotpModalOpen}
            isEnabled={isTOTP}
            enabled={enabled}
          />

          <AddSmsModal
            open={smsModalOpen}
            onOpenChange={setSmsModalOpen}
            isEnabled={isSMS}
            enabled={enabled}
          />
          {!isEmail && (
            <Button variant="link" onClick={handleAddEmail}>
              + Add Email
            </Button>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
